import {validateTextField} from '@github-ui/entity-validators'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {TextInput} from '@primer/react'
import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import type {IssueFormElementInputNode} from '../create-issue-query-types'
import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {safeGetDefaultValue} from '../utils/default-value'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import {ElementWrapper} from './ElementWrapper'
import styles from './TextInputElement.module.css'

const DEBOUNCE_TIMEOUT_MS = 500

// RELAY-CLEANUP(useHandleTemplateChangeIssueForm): widens nullable fields so the still-Relay parent
// data is assignable. Remove when useHandleTemplateChangeIssueForm leaves Relay.
export type TextInputElementFragment = RelayCompat<
  Pick<IssueFormElementInputNode, 'itemId' | 'label' | 'descriptionHTML' | 'placeholder' | 'value' | 'required'>
>

export type TextInputElementInternalProps = GraphqlFragment<TextInputElementFragment> & {
  type: 'input'
  index?: number
  ref?: React.Ref<IssueFormElementRef>
}

type TextInputElementProps = TextInputElementFragment & {
  index?: number
  ref?: React.Ref<IssueFormElementRef>
} & SharedElementProps

export const TextInputElement = ({
  ref,
  index,
  sessionStorageKey,
  itemId,
  label,
  descriptionHTML,
  placeholder,
  value,
  required,
  ...props
}: TextInputElementProps) => {
  // Fall back to the element index when itemId is null so multiple inputs don't share a storage key.
  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', label, itemId ?? index ?? null)
  return (
    <TextInputElementInternal
      {...props}
      index={index}
      type={'input'}
      sessionStorageKey={elementStorageKey}
      ref={ref}
      itemId={itemId}
      label={label}
      descriptionHTML={descriptionHTML}
      placeholder={placeholder}
      value={value}
      required={required}
    />
  )
}

export const TextInputElementInternal = ({
  ref,
  sessionStorageKey,
  itemId,
  index: indexOfFormElement,
  label,
  descriptionHTML,
  placeholder,
  required,
  value: templateDefaultValue,
  defaultValuesById,
  onFieldChange,
}: TextInputElementInternalProps & SharedElementProps) => {
  const defaultValue = useMemo(
    () => safeGetDefaultValue({id: itemId, indexOfFormElement, value: templateDefaultValue, defaultValuesById}),
    [itemId, indexOfFormElement, templateDefaultValue, defaultValuesById],
  )

  const [textValue, setValue] = useSessionStorage<string>(sessionStorageKey, defaultValue)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Updates value with new default values from defaultValuesById
  useEffect(() => {
    const hasDefaultValues = defaultValuesById && Object.keys(defaultValuesById).length > 0
    if (onFieldChange && hasDefaultValues && !isUserTyping) {
      const newDefaultValue = safeGetDefaultValue({
        id: itemId,
        indexOfFormElement,
        value: templateDefaultValue,
        defaultValuesById,
      })

      // Only update if the new default value is different from current value
      // and it's not the result of the user's own typing being reflected back.
      // Normalize templateDefaultValue (which can be null from GraphQL) to match
      // the fallback used by safeGetDefaultValue (which returns value ?? '').
      const normalizedTemplateDefault = templateDefaultValue ?? ''
      if (newDefaultValue !== textValue && newDefaultValue !== normalizedTemplateDefault) {
        setValue(newDefaultValue)
      }
    }
  }, [
    defaultValuesById,
    indexOfFormElement,
    isUserTyping,
    itemId,
    onFieldChange,
    setValue,
    templateDefaultValue,
    textValue,
  ])

  // Handler for text changes that updates both local state and parent component
  const handleTextChange = (value: string) => {
    setIsUserTyping(true)
    setValue(value)

    if (onFieldChange) {
      // Since these are direct changes, they should always be sent up, regardless of
      // current state
      onFieldChange(itemId, indexOfFormElement, value)
    }

    // Clear existing timeout and set a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Reset typing flag after a longer delay to prevent race conditions
    timeoutRef.current = setTimeout(() => {
      setIsUserTyping(false)
      timeoutRef.current = null
    }, DEBOUNCE_TIMEOUT_MS)
  }

  const inputRef = useRef<HTMLInputElement>(null)
  const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, textValue),
      initialMarkdown: () => prefixMarkdownTitle(label, templateDefaultValue ?? ''),
      validate: () => {
        const validity = validateTextField(textValue, required ?? false)
        setValidationResult(validity.errorMessage)
        return validity.isValid
      },
      reset: () => {
        setValue(defaultValue)
        setValidationResult(undefined)
      },
      hasChanges: () => textValue !== defaultValue,
      getSessionStorageKey: () => sessionStorageKey,
      type: 'input' as const,
    }),
    [defaultValue, label, required, sessionStorageKey, setValue, templateDefaultValue, textValue],
  )

  useEffect(() => {
    // If we had a validation error and the user has fixed it, clear the error
    if (validationResult && validateTextField(textValue, required ?? false).isValid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValidationResult(undefined)
    }
  }, [required, textValue, validationResult])

  return (
    <ElementWrapper label={label} description={descriptionHTML} required={required} validationResult={validationResult}>
      {({labelId, descriptionIds}) => (
        <TextInput
          ref={inputRef}
          aria-labelledby={labelId}
          aria-describedby={descriptionIds}
          placeholder={placeholder ?? undefined}
          value={textValue}
          className={styles.issueFormTextField}
          onChange={e => {
            handleTextChange(e.target.value)
          }}
        />
      )}
    </ElementWrapper>
  )
}

TextInputElement.displayName = 'TextInputElement'
TextInputElementInternal.displayName = 'TextInputElementInternal'
